import type { ReactNode } from 'react';

type MarkdownBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'unordered-list'; items: string[] }
  | { type: 'ordered-list'; items: string[] }
  | { type: 'heading'; level: 1 | 2 | 3 | 4; text: string }
  | { type: 'blockquote'; text: string }
  | { type: 'code'; code: string; language: string | null }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'rule' };

export function Route42Markdown({ content, className = '' }: { content: string; className?: string }) {
  const blocks = parseBlocks(content);
  return (
    <div className={`r42-markdown${className ? ` ${className}` : ''}`}>
      {blocks.map((block, index) => renderBlock(block, index))}
    </div>
  );
}

export function Route42InlineMarkdown({ content }: { content: string }) {
  return <>{renderInline(content)}</>;
}

function renderBlock(block: MarkdownBlock, index: number): ReactNode {
  const key = `${block.type}-${index}`;
  if (block.type === 'paragraph') return <p key={key}>{renderInline(block.text)}</p>;
  if (block.type === 'unordered-list') return <ul key={key}>{block.items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`}>{renderInline(item)}</li>)}</ul>;
  if (block.type === 'ordered-list') return <ol key={key}>{block.items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`}>{renderInline(item)}</li>)}</ol>;
  if (block.type === 'blockquote') return <blockquote key={key}>{renderInline(block.text)}</blockquote>;
  if (block.type === 'code') return <pre key={key}><code data-language={block.language ?? undefined}>{block.code}</code></pre>;
  if (block.type === 'table') return <div className="r42-markdown-table-wrap" key={key}><table><thead><tr>{block.headers.map((header, cellIndex) => <th key={`${key}-head-${cellIndex}`}>{renderInline(header)}</th>)}</tr></thead><tbody>{block.rows.map((row, rowIndex) => <tr key={`${key}-row-${rowIndex}`}>{row.map((cell, cellIndex) => <td key={`${key}-cell-${rowIndex}-${cellIndex}`}>{renderInline(cell)}</td>)}</tr>)}</tbody></table></div>;
  if (block.type === 'rule') return <hr key={key} />;
  if (block.level === 1) return <h1 key={key}>{renderInline(block.text)}</h1>;
  if (block.level === 2) return <h2 key={key}>{renderInline(block.text)}</h2>;
  if (block.level === 3) return <h3 key={key}>{renderInline(block.text)}</h3>;
  return <h4 key={key}>{renderInline(block.text)}</h4>;
}

function parseBlocks(content: string): MarkdownBlock[] {
  const lines = String(content ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const fence = line.match(/^\s*```([\w-]+)?\s*$/);
    if (fence) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !/^\s*```\s*$/.test(lines[index])) {
        code.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push({ type: 'code', code: code.join('\n'), language: fence[1] ?? null });
      continue;
    }

    const heading = line.match(/^\s*(#{1,4})\s+(.+?)\s*$/);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length as 1 | 2 | 3 | 4, text: heading[2] });
      index += 1;
      continue;
    }

    if (/^\s*(?:---+|___+|\*\*\*+)\s*$/.test(line)) {
      blocks.push({ type: 'rule' });
      index += 1;
      continue;
    }

    if (index + 1 < lines.length && isTableHeader(line, lines[index + 1])) {
      const headers = splitTableRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && lines[index].trim() && lines[index].includes('|')) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^\s*>\s?/.test(lines[index])) {
        quote.push(lines[index].replace(/^\s*>\s?/, '').trim());
        index += 1;
      }
      blocks.push({ type: 'blockquote', text: quote.join(' ') });
      continue;
    }

    const unordered = line.match(/^\s*[-+*]\s+(.+)$/);
    if (unordered) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = lines[index].match(/^\s*[-+*]\s+(.+)$/);
        if (!match) break;
        items.push(match[1].trim());
        index += 1;
      }
      blocks.push({ type: 'unordered-list', items });
      continue;
    }

    const ordered = line.match(/^\s*\d+[.)]\s+(.+)$/);
    if (ordered) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = lines[index].match(/^\s*\d+[.)]\s+(.+)$/);
        if (!match) break;
        items.push(match[1].trim());
        index += 1;
      }
      blocks.push({ type: 'ordered-list', items });
      continue;
    }

    const paragraph: string[] = [line.trim()];
    index += 1;
    while (index < lines.length && lines[index].trim() && !startsBlock(lines[index])) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
  }

  return blocks;
}

function startsBlock(line: string): boolean {
  return /^\s*(?:```|#{1,4}\s+|>\s?|[-+*]\s+|\d+[.)]\s+|---+\s*$|___+\s*$|\*\*\*+\s*$)/.test(line);
}

function isTableHeader(line: string, separator: string): boolean {
  if (!line.includes('|') || !separator.includes('|')) return false;
  const cells = splitTableRow(separator);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s+/g, '')));
}

function splitTableRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

function renderInline(value: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\*\*[^*\n]+\*\*|__[^_\n]+__|~~[^~\n]+~~|`[^`\n]+`|\[[^\]\n]+\]\((?:https?:\/\/|mailto:)[^)\s]+\)|\*[^*\n]+\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(value)) !== null) {
    if (match.index > lastIndex) nodes.push(value.slice(lastIndex, match.index));
    const token = match[0];
    const nodeKey = `inline-${key++}`;

    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(<strong key={nodeKey}>{renderInline(token.slice(2, -2))}</strong>);
    } else if (token.startsWith('__') && token.endsWith('__')) {
      nodes.push(<strong key={nodeKey}>{renderInline(token.slice(2, -2))}</strong>);
    } else if (token.startsWith('~~') && token.endsWith('~~')) {
      nodes.push(<del key={nodeKey}>{renderInline(token.slice(2, -2))}</del>);
    } else if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(<code key={nodeKey}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('[')) {
      const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) {
        const external = /^https?:\/\//i.test(link[2]);
        nodes.push(<a key={nodeKey} href={link[2]} target={external ? '_blank' : undefined} rel={external ? 'noreferrer noopener' : undefined}>{renderInline(link[1])}</a>);
      } else {
        nodes.push(token);
      }
    } else {
      nodes.push(<em key={nodeKey}>{renderInline(token.slice(1, -1))}</em>);
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < value.length) nodes.push(value.slice(lastIndex));
  return nodes;
}
